// GtPortal: the candidate's walled space. The sidebar is the CRM's own shell
// (gt-root, gt-nav). Every section sits under one profile header built on the
// CRM record layout (gtr2): cover, avatar, name, chips, actions and a stat
// strip, so a candidate's portal reads like their record does to us.
// P2-PORTAL (22 Oct 2026)
import { useState, useEffect, useCallback } from 'react';
import { useAuth, useUser, UserButton } from '@clerk/clerk-react';
import API_BASE from '../../config';
import PortalJobs from './portal/PortalJobs';
import PortalApplications from './portal/PortalApplications';
import PortalInterviews from './portal/PortalInterviews';
import PortalLexa from './portal/PortalLexa'; // C1-LEXA3
import PortalSettings from './portal/PortalSettings';
import { Ico, ICONS, initials, ukDate, readLexaIntent, setLexaIntent } from './portal/portalKit';

const MENUS = ['Jobs', 'Applications', 'Interviews', 'Lexa', 'Settings'];
const MENU_ICON = {
  Jobs: 'M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2 M4 7h16a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a1 1 0 0 1 1-1z M3 12h18',
  Applications: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M9 13h6 M9 17h6 M9 9h1',
  Interviews: 'M21 12a8 8 0 0 1-8 8H4l2.5-2.5A8 8 0 1 1 21 12z M8.5 12h.01 M12 12h.01 M15.5 12h.01',
  Lexa: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z',
  Settings: 'M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M1.5 14h5 M9.5 8h5 M17.5 16h5',
};
const MENU_LABEL = { Settings: 'Profile' };
const SECTION_SUB = {
  Jobs: 'Roles we are recruiting for now',
  Applications: 'Where each of your applications stands',
  Interviews: 'Your interviews with Nathan, our AI interviewer',
  Lexa: 'Your AI career and interview coach',
  Settings: 'Your CV, preferences and data',
};

function NavIcon({ name }) {
  return (
    <svg className="gt-nav-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {MENU_ICON[name].split(' M').map((d, i) => <path key={i} d={(i ? 'M' : '') + d} />)}
    </svg>
  );
}

function startSection() {
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('lexa')) return 'Lexa';
  } catch (e) { /* fall through */ }
  return readLexaIntent() ? 'Lexa' : 'Jobs';
}

export default function GtPortal() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const [active, setActive] = useState(startSection);
  const [sum, setSum] = useState(null);

  // One read of the numbers the header shows. Each section still loads its own
  // detail; this is only the strip, and it is refreshed when a section changes
  // something (an application, a Lexa session, a new CV).
  const loadSummary = useCallback(async () => {
    const token = await getToken();
    const get = async (p) => {
      try {
        const r = await fetch(API_BASE + p, { headers: { Authorization: 'Bearer ' + token } });
        return r.ok ? r.json() : null;
      } catch (e) { return null; }
    };
    const [p, a, i, l] = await Promise.all([
      get('/api/portal/profile'), get('/api/portal/applications'),
      get('/api/portal/interviews'), get('/api/portal/lexa/sessions'),
    ]);
    setSum({
      profile: (p && p.profile) || null,
      applications: (a && a.applications) || [],
      interviews: (i && i.interviews) || [],
      lexa: (l && l.sessions) || [],
    });
    return p && p.profile;
  }, [getToken]);

  // Without a CV there is nothing to apply with, so send them where the upload
  // is. Only moves off the default, so a deliberate click is never overridden.
  useEffect(() => {
    let live = true;
    loadSummary().then((profile) => {
      if (live && profile && profile.cv && !profile.cv.has_cv) setActive((a) => (a === 'Jobs' ? 'Settings' : a));
    });
    return () => { live = false; };
  }, [loadSummary]);

  const go = useCallback((section, lexa) => {
    if (lexa) setLexaIntent(lexa.kind, lexa.role);
    setActive(section);
    try { document.querySelector('.gtpt-main').scrollTo({ top: 0 }); } catch (e) { /* fine */ }
  }, []);

  const name = (user && (user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' '))) || 'Your profile';
  const email = (sum && sum.profile && sum.profile.email) || (user && user.primaryEmailAddress && user.primaryEmailAddress.emailAddress) || '';
  const firstName = (user && user.firstName) || 'there';

  return (
    <div className="gt-root gt-root--portal gtp2-shell">
      <nav className="gt-nav">
        <div className="gt-brand gtpt-brand">
          <img src="/favicon-512.png" alt="Generative Talent Co" className="gtpt-brand-mark" />
        </div>
        {MENUS.map((m) => (
          <button key={m} type="button"
            className={'gt-nav-item' + (active === m ? ' gt-nav-item--active' : '')}
            onClick={() => go(m)} aria-current={active === m ? 'page' : undefined}>
            <NavIcon name={m} />
            {MENU_LABEL[m] || m}
          </button>
        ))}
        <div className="gt-nav-foot"><UserButton afterSignOutUrl="/" /></div>
      </nav>

      <main className="gt-main gtpt-main">
        <div className="gtpt-scroll">
          <div className="gtr2 gtp2">
            <PortalHero name={name} email={email} sum={sum} active={active} go={go} />

            {active === 'Jobs' && <PortalJobs getToken={getToken} firstName={firstName} go={go} onChanged={loadSummary} onNeedCv={() => go('Settings')} />}
            {active === 'Applications' && <PortalApplications getToken={getToken} go={go} />}
            {active === 'Interviews' && <PortalInterviews getToken={getToken} go={go} />}
            {active === 'Lexa' && <PortalLexa getToken={getToken} go={go} onChanged={loadSummary} onNeedCv={() => go('Settings')} />}
            {active === 'Settings' && <PortalSettings getToken={getToken} user={user} onChanged={loadSummary} />}
          </div>
        </div>
      </main>

      <nav className="gtpt-tabbar" aria-label="Candidate menu">
        {MENUS.map((m) => (
          <button key={m} type="button"
            className={'gtpt-tab' + (active === m ? ' gtpt-tab--on' : '')}
            onClick={() => go(m)} aria-current={active === m ? 'page' : undefined}>
            <NavIcon name={m} />
            <span>{MENU_LABEL[m] || m}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function PortalHero({ name, email, sum, active, go }) {
  const p = (sum && sum.profile) || {};
  const res = p.location_residence || {};
  const place = [res.city, res.country].filter(Boolean).join(', ');
  const looking = !p.communications || p.communications.looking_for_work !== false;
  const hasCv = !!(p.cv && p.cv.has_cv);
  const apps = sum ? sum.applications : null;
  const ivs = sum ? sum.interviews : null;
  const lexa = sum ? sum.lexa : null;
  const openIv = ivs ? ivs.filter((x) => x.status === 'open').length : null;
  const liveApps = apps ? apps.filter((a) => a.status !== 'closed').length : null;
  const doneLexa = lexa ? lexa.filter((s) => s.status === 'completed').length : null;
  const n = (v) => (v === null ? '…' : v);

  return (
    <section className="gtr2-hero gtp2-hero">
      <div className="gtr2-cover" />
      <div className="gtr2-hero-row">
        <span className="gtr2-avatar gtp2-avatar">{initials(name)}</span>
        <div className="gtr2-id">
          <div className="gtr2-namerow">
            <h1 className="gtr2-name">{name}</h1>
            {sum && sum.profile && (
              <span className={'gtt-pill gtr2-status ' + (looking ? 'gtp2-pill--open' : 'gtp2-pill--paused')}>
                {looking ? 'Open to roles' : 'Not looking right now'}
              </span>
            )}
          </div>
          <p className="gtr2-headline">{SECTION_SUB[active]}</p>
          <div className="gtr2-chips">
            {place && <span className="gtr2-chip"><Ico d={ICONS.pin} />{place}</span>}
            {email && <span className="gtr2-chip"><Ico d={ICONS.mail} />{email}</span>}
            {sum && sum.profile && (
              <button type="button" className={'gtr2-chip gtp2-chipbtn' + (hasCv ? ' gtr2-chip--link' : ' gtp2-chip--warn')} onClick={() => go('Settings')}>
                <Ico d={ICONS.doc} />{hasCv ? 'CV on file' : 'Add your CV'}
              </button>
            )}
            {p.ref && <span className="gtr2-ref">{p.ref}</span>}
          </div>
        </div>
        <div className="gtr2-acts">
          {active !== 'Jobs' && <button type="button" className="gtr2-btn gtr2-btn--primary" onClick={() => go('Jobs')}><Ico d={ICONS.search} />Browse roles</button>}
          {active !== 'Lexa' && <button type="button" className="gtr2-btn" onClick={() => go('Lexa')}><Ico d={ICONS.spark} />Prepare with Lexa</button>}
          {active !== 'Settings' && <button type="button" className="gtr2-btn" onClick={() => go('Settings')}><Ico d={ICONS.user} />Edit profile</button>}
        </div>
      </div>
      <div className="gtr2-stats gtp2-stats">
        <button type="button" className="gtr2-stat gtr2-stat--go" onClick={() => go('Applications')}>
          <span>Applications</span><b>{n(liveApps)}</b><em>{apps && apps.length !== liveApps ? apps.length + ' in all' : 'in progress'}</em>
        </button>
        <button type="button" className="gtr2-stat gtr2-stat--go" onClick={() => go('Interviews')}>
          <span>Interviews</span><b>{n(openIv)}</b><em>waiting for you</em>
        </button>
        <button type="button" className="gtr2-stat gtr2-stat--go" onClick={() => go('Lexa')}>
          <span>Lexa sessions</span><b>{n(doneLexa)}</b><em>with reports</em>
        </button>
        <button type="button" className="gtr2-stat gtr2-stat--go" onClick={() => go('Settings')}>
          <span>Your CV</span><b>{sum ? (hasCv ? 'On file' : 'Missing') : '…'}</b><em>{hasCv && p.cv.updated_at ? 'updated ' + ukDate(p.cv.updated_at) : 'needed to apply'}</em>
        </button>
        <div className="gtr2-stat">
          <span>Member since</span><b>{p.member_since ? ukDate(p.member_since) : '…'}</b>
        </div>
      </div>
    </section>
  );
}
